/**
 * GuiaHost — o guia passo a passo no painel (protótipo "Fluxo completo · do Início sem listas (B2)
 * aos contatos", Claude Design, 28/10/2026; conhecimento/analises/PROPOSTA-GUIA-PRIMEIROS-LEADS.md).
 * Montado no AppShell, como os outros hosts: a janela do convite e da escolha da busca, o cartão
 * (ou a pílula), o balão de cada passo com o anel no elemento, o toast de "Sair do guia", o balão
 * que mostra o menu Conta depois de "Agora não" e o "Fale conosco" do "Preciso de ajuda".
 *
 * Os textos de cada passo saem daqui; quando cada passo avança, de useGuiaControle.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBrand } from '../../hooks/useBrand';
import { useSessionStore } from '../../stores/sessionStore';
import { useCaptureStore } from '../../stores/captureStore';
import { useUiStore } from '../../stores/uiStore';
import { EXTENSION_MODAL_ID } from '../../hooks/useRequireExtension';
import { isTrial, level } from '../../lib/entitlements';
import { emitActivity } from '../../lib/activity';
import { formatDate } from '../../lib/format';
import { ContactDialog } from '../contact/ContactDialog';
import { useCtaStore } from '../account/ctaStore';
import { openRenewal } from '../account/renewal';
import { userPlace } from '../onboarding/place';
import { useGuiaStore } from './guiaStore';
import { comecarGuia, reabrirBusca } from './guiaAcoes';
import { GuiaJanela } from './GuiaJanela';
import { GuiaCartao, type ConteudoCartao } from './GuiaCartao';
import { GuiaBalao, type BalaoDef } from './GuiaBalao';
import { useGuiaText } from './guiaText';
import { marcoDe, passoQuieto, proximoCampo, isCampo, type PassoId } from './passos';
import { paginaDeListas, useGuiaControle } from './useGuiaControle';
import './guia.css';

/** Abaixo disto, com a janela de captura aberta, o cartão começa recolhido (ajuste 7). */
const LARGURA_ESTREITA = 1200;
/** O botão da janela de captura, que o guia aponta no passo "comecar". */
const ALVO_COMECAR = '[data-guia="botao-comecar"]';
/**
 * O que o cartão evita cobrir, além do elemento do passo: os dois botões amarelos da janela de
 * captura ("Começar a captura" e "Abrir a aba e começar") e o balão.
 */
const NAO_COBRIR = [ALVO_COMECAR, '.ic-abrir', '.gp-balao'];

function useLarguraDaTela(): number {
  const [largura, setLargura] = useState(() =>
    typeof window === 'undefined' ? 1440 : window.innerWidth,
  );
  useEffect(() => {
    const onResize = (): void => setLargura(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return largura;
}

export function GuiaHost(): JSX.Element | null {
  const { tx } = useGuiaText();
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useBrand();
  const session = useSessionStore((s) => s.session);
  const guia = useGuiaStore();
  const captureWindow = useCaptureStore((s) => s.captureWindow);
  const vivo = useGuiaControle();
  const largura = useLarguraDaTela();
  const [expandido, setExpandido] = useState<PassoId | null>(null);
  const [contato, setContato] = useState(false);
  const cidade = useMemo(() => userPlace().city ?? '', []);

  // O toast de "Sair do guia" fica 8 s (com "Desfazer"), como os toasts com ação.
  useEffect(() => {
    if (!guia.saida) return undefined;
    const id = window.setTimeout(() => useGuiaStore.getState().fecharToast(), 8000);
    return () => window.clearTimeout(id);
  }, [guia.saida]);

  const ligado = guia.status === 'ativo';
  const passo = guia.passo;
  const modulo = guia.modulo;
  const marco = marcoDe(passo);

  const conteudo = ligado ? conteudoDoPasso() : null;
  const balao =
    ligado && !guia.janela && !guia.recolhido && !passoQuieto(passo) ? balaoDoPasso() : null;
  const autoRecolher =
    Boolean(captureWindow) && largura < LARGURA_ESTREITA && (isCampo(passo) || passo === 'comecar');
  const pilula = guia.recolhido || ((passoQuieto(passo) || autoRecolher) && expandido !== passo);

  return (
    <>
      <GuiaJanela />
      {ligado && !guia.janela ? (
        <GuiaCartao
          marco={marco}
          chave={passo}
          conteudo={conteudo}
          pilula={pilula}
          evitar={balao ? [...NAO_COBRIR, balao.alvo] : NAO_COBRIR}
          onAbrir={() => {
            guia.recolher(false);
            setExpandido(passo);
          }}
          onRecolher={() => {
            guia.recolher(true);
            setExpandido(null);
          }}
          onAjuda={() => {
            emitActivity('guide.help_requested', { step: passo });
            setContato(true);
          }}
          onRecomecar={() => comecarGuia(guia.origem, guia.perfil)}
          onSair={() => {
            emitActivity('guide.exited', { step: passo });
            guia.sair();
          }}
        />
      ) : null}
      {balao ? <GuiaBalao key={`${passo}:${balao.alvo}`} def={balao} /> : null}
      {guia.balaoConta && !ligado ? (
        <GuiaBalao
          key="conta"
          def={{
            alvo: '#menu_account',
            lado: 'baixo',
            titulo: tx('b_account'),
            texto: tx('b_account_text'),
            largura: 280,
            entendi: { rotulo: tx('ok'), acao: () => guia.fecharBalaoConta() },
          }}
        />
      ) : null}
      {guia.saida ? (
        <div className="gp-camada">
          <div className="gp-toast ds-entra" role="status">
            {tx('toast_exit')}
            <button type="button" onClick={() => guia.desfazerSaida()}>
              {tx('undo')}
            </button>
          </div>
        </div>
      ) : null}
      {contato ? (
        <ContactDialog onClose={() => setContato(false)} initialSubject="problem" />
      ) : null}
    </>
  );

  function janelaFechada(reabrir: PassoId): ConteudoCartao {
    return {
      frase: tx('closed'),
      botao: { rotulo: tx('reopen'), onClick: () => reabrirBusca(reabrir) },
    };
  }

  function conteudoDoPasso(): ConteudoCartao {
    const naJanela = Boolean(captureWindow);
    switch (passo) {
      case 'escolher':
        return {
          frase: tx('esc_title'),
          botao: { rotulo: tx('esc_open'), onClick: () => guia.abrirEscolha() },
        };
      case 'tipo':
        return { frase: tx('pick_type'), espera: tx('wait_window') };
      case 'extensao':
        return {
          frase: tx('ext_title'),
          texto: tx('ext_text'),
          botao: {
            rotulo: tx('ext_install'),
            onClick: () => useUiStore.getState().openModal(EXTENSION_MODAL_ID),
          },
        };
      case 'licenca':
        return {
          frase: tx('license_title'),
          texto: session?.expiredAt
            ? tx('license_text', { date: formatDate(session.expiredAt) })
            : undefined,
          botao: {
            rotulo: tx('renew'),
            onClick: () => openRenewal(brand.source, session?.email ?? '', 'guide'),
          },
        };
      case 'comecar':
        return naJanela
          ? { frase: tx('start'), espera: tx('wait_start') }
          : janelaFechada('comecar');
      case 'explicacao':
        return { frase: tx('tab_notice') };
      case 'buscando':
        return {
          frase: tx('running'),
          texto: tx('running_text'),
          conta: vivo.contatos,
          aviso: vivo.parado ? { titulo: tx('stuck_title'), texto: tx('stuck_text') } : undefined,
        };
      case 'nada':
        return {
          frase: tx('empty'),
          texto: tx('empty_text'),
          botao: { rotulo: tx('adjust'), onClick: () => reabrirBusca('campo_termo') },
        };
      case 'aba_fechada':
        return {
          frase: tx('tab_closed'),
          texto: tx('tab_closed_text'),
          botao: { rotulo: tx('resume'), onClick: () => reabrirBusca('comecar') },
        };
      case 'aviso_teste':
        return {
          frase: tx('trial'),
          texto: tx('trial_text'),
          botao: { rotulo: tx('ok'), onClick: () => guia.ir('abrir_lista') },
        };
      case 'abrir_lista': {
        if (modulo && location.pathname === paginaDeListas(modulo)) {
          return { frase: tx('open_list'), espera: tx('wait_list') };
        }
        return {
          frase: listaChegou(),
          botao: modulo
            ? { rotulo: tx('see_list'), onClick: () => navigate(paginaDeListas(modulo)) }
            : undefined,
        };
      }
      case 'fim':
        return {
          ok: true,
          frase: tituloDoFim(),
          texto: tx('end_text'),
          dica: dicaDoFim(),
          botao: {
            rotulo: tx('new_search'),
            onClick: () => {
              guia.encerrar();
              useCaptureStore.getState().openCategories();
            },
          },
          botao2: { rotulo: tx('close_guide'), onClick: () => guia.encerrar() },
        };
      default:
        // Campos da busca
        return naJanela
          ? { frase: tx('fields'), link: { rotulo: tx('skip'), onClick: () => guia.ir('comecar') } }
          : janelaFechada(passo);
    }
  }

  function listaChegou(): string {
    return vivo.contatos === 1 ? tx('list_arrived_one') : tx('list_arrived', { n: vivo.contatos });
  }

  function tituloDoFim(): string {
    const n = vivo.contatos;
    const termo = vivo.lista?.keywords?.[0] ?? guia.termo;
    const lugar = vivo.lista?.locations?.[0]?.split('|')[1] ?? '';
    if (!n || !termo) return tx('end_title_plain');
    if (lugar)
      return n === 1
        ? tx('end_title_where_one', { term: termo, place: lugar })
        : tx('end_title_where', { n, term: termo, place: lugar });
    return n === 1 ? tx('end_title_one', { term: termo }) : tx('end_title', { n, term: termo });
  }

  function dicaDoFim(): ConteudoCartao['dica'] {
    if (isTrial(session)) {
      return {
        texto: tx('end_tip_trial'),
        acao: { rotulo: tx('see_plans'), onClick: () => useCtaStore.getState().openCTA('default') },
      };
    }
    if (modulo === 'places' && level(session, 'enrich.places') === 'on')
      return { texto: tx('end_tip_enrich') };
    return undefined;
  }

  function balaoDoPasso(): BalaoDef | null {
    if (!modulo) return null;
    const proximo = (): void => guia.ir(proximoCampo(modulo, passo));
    const entendi = { rotulo: tx('ok'), acao: proximo };
    const places = modulo === 'places';
    switch (passo) {
      case 'campo_termo':
        return {
          alvo: '[data-guia="campo-palavras"] .tags',
          lado: 'esq',
          titulo: tx('b_term'),
          texto: places
            ? guia.termo
              ? tx('b_term_places', { term: guia.termo })
              : tx('b_term_places_empty')
            : guia.termo
              ? tx('b_term_sites', { term: guia.termo })
              : tx('b_term_sites_empty'),
          entendi,
        };
      case 'campo_lugar':
        return {
          alvo: '[data-guia="campo-locais"] .tags',
          lado: 'esq',
          titulo: tx('b_place'),
          texto: guia.termo && cidade ? tx('b_place_city', { city: cidade }) : tx('b_place_empty'),
          entendi,
        };
      case 'campo_alcance':
        return {
          alvo: '[data-guia="campo-alcance"]',
          lado: 'esq',
          titulo: tx('b_reach'),
          texto: tx('b_reach_text'),
          entendi,
        };
      case 'campo_fontes':
        return {
          alvo: '[data-guia="campo-fontes"] .tags',
          lado: 'esq',
          titulo: tx('b_sources'),
          texto: tx('b_sources_text'),
          entendi,
        };
      case 'campo_ddd':
        return {
          alvo: '[data-guia="campo-ddd"]',
          lado: 'esq',
          titulo: tx('b_area'),
          texto: tx('b_area_text'),
          entendi,
        };
      case 'comecar':
        return {
          alvo: ALVO_COMECAR,
          lado: 'cima',
          titulo: tx('b_start'),
          texto: tx('b_start_text'),
        };
      case 'abrir_lista':
        if (location.pathname !== paginaDeListas(modulo)) return null;
        return {
          alvo: guia.listaId ? `[data-guia-lista="${guia.listaId}"]` : '[data-guia-lista]',
          lado: 'baixo',
          titulo: listaChegou(),
          texto: tx('b_list_text'),
          largura: 260,
        };
      default:
        return null;
    }
  }
}
